/**
 * Cross-table consistency helpers.
 *
 * syncLotToSource() is the entry point: when a card or sealed lot is saved with
 * a Source ID that points at an existing Purchase (PUR-…) or Adjustment (TXN-…),
 * ensure that parent's Items[] array contains an entry for the lot.
 *
 * Dedup key is the Lot ID. Two lots with the same name but different Lot IDs
 * (e.g. two copies of the same booster box bought separately) get separate
 * Items entries. If an Items entry for this Lot ID already exists, its Qty and
 * UnitCost/UnitBasis are refreshed from the lot (so editing a sealed lot's
 * quantity from 1 to 6 bumps the parent entry to ×6); descriptive fields are
 * left untouched so hand-edits on the parent form aren't clobbered.
 */
import { DB } from "./db";
import type { Row } from "./types";

export type LotKind = "Card" | "Sealed";

/**
 * Route a lot to its source parent based on the Source ID prefix.
 * No-op when Source ID is blank or points at something without an Items array.
 */
export function syncLotToSource(lotId: string, kind: LotKind, row: Row): void {
  const sourceId = row["Source ID"];
  if (!sourceId || typeof sourceId !== "string") return;
  if (sourceId.startsWith("PUR-")) syncLotToPurchase(lotId, kind, row);
  else if (sourceId.startsWith("TXN-")) syncLotToAdjustment(lotId, kind, row);
  // R- (rips) and TR- (trades) record their lots through their own save flows,
  // so they're intentionally skipped here.
}

/**
 * If the given lot's Source ID points at an existing Purchase, append an Items
 * entry for the lot (only when it isn't already present). Recomputes the
 * purchase's Subtotal and Total Paid.
 */
export function syncLotToPurchase(lotId: string, kind: LotKind, row: Row): void {
  const sourceId = row["Source ID"];
  if (!sourceId || typeof sourceId !== "string") return;
  if (!sourceId.startsWith("PUR-")) return;

  const purchase = DB.table("purchases").find(p => p["Purchase ID"] === sourceId);
  if (!purchase) return;

  const items: any[] = Array.isArray(purchase.Items) ? purchase.Items.slice() : [];
  const qty      = Number(row["Qty"]) || 1;
  const unitCost = Number(row["Unit Basis"]) || 0;

  // Find the existing entry for this Lot ID (if any). Dedup is strictly by
  // Lot ID — same name but a different Lot ID counts as a separate entry.
  const existingIdx = items.findIndex(it => it["Lot ID"] === lotId);

  if (existingIdx >= 0) {
    // Refresh Qty + UnitCost from the lot. If nothing changed, skip.
    const cur = items[existingIdx];
    if ((Number(cur.Qty) || 0) === qty && (Number(cur.UnitCost) || 0) === unitCost) return;
    items[existingIdx] = { ...cur, Qty: qty, UnitCost: unitCost };
  } else {
    // Build a brand-new Item entry from the lot's fields and append.
    let item: Record<string, any>;
    if (kind === "Sealed") {
      item = {
        "Type":        "Sealed",
        "Game":        row["Game"] || "",
        "Item":        row["Product"] || "",
        "Set":         row["Set"] || "",
        "Number":      "",
        "Condition":   "",
        "Grade":       "",
        "ProductType": row["Product Type"] || "",
        "Qty":         qty,
        "UnitCost":    unitCost,
        "Lot ID":      lotId,
      };
    } else {
      const isSlab = String(row["Inventory Class"] || "").toLowerCase() === "slab";
      item = {
        "Type":        isSlab ? "Graded" : "Raw",
        "Game":        row["Game"] || "",
        "Item":        row["Card Name"] || "",
        "Set":         row["Set"] || "",
        "Number":      row["Card #"] || "",
        "Condition":   isSlab ? "" : (row["Condition/Grade"] || "Near Mint"),
        "Grade":       isSlab ? (row["Condition/Grade"] || "") : "",
        "ProductType": "",
        "Qty":         qty,
        "UnitCost":    unitCost,
        "Lot ID":      lotId,
      };
    }
    items.push(item);
  }

  const subtotal = items.reduce((s, x) => s + (Number(x.Qty) || 0) * (Number(x.UnitCost) || 0), 0);
  const fees = Number(purchase["Fees"]) || 0;

  DB.update("purchases", "Purchase ID", sourceId, {
    "Items":      items,
    "Subtotal":   +subtotal.toFixed(2),
    "Total Paid": +(subtotal + fees).toFixed(2),
  });
}

/**
 * Same idea for Adjustments (TXN-… Source IDs). The adjustment's Items[] array
 * uses the contributed-items shape: { AsType, Game, Item, Set, Number,
 * Condition, ProductType, Qty, UnitBasis, "Lot ID" }. Adjustments have no
 * Subtotal/Total to recompute, so we just maintain the Items array.
 */
export function syncLotToAdjustment(lotId: string, kind: LotKind, row: Row): void {
  const sourceId = row["Source ID"];
  if (!sourceId || typeof sourceId !== "string") return;
  if (!sourceId.startsWith("TXN-")) return;

  const adj = DB.table("transactions").find(t => t["Txn ID"] === sourceId);
  if (!adj) return;
  // Only items-in adjustments (Owner Contribution) accept reverse sync. Gifts
  // are outflows and their Items[] is managed by the gift form's lot picker,
  // so a card pointing back at a Gift via Source ID shouldn't append there.
  if (adj["Type"] !== "Owner Contribution") return;

  const items: any[] = Array.isArray(adj.Items) ? adj.Items.slice() : [];
  const qty       = Number(row["Qty"]) || 1;
  const unitBasis = Number(row["Unit Basis"]) || 0;

  const existingIdx = items.findIndex(it => it["Lot ID"] === lotId);
  if (existingIdx >= 0) {
    const cur = items[existingIdx];
    if ((Number(cur.Qty) || 0) === qty && (Number(cur.UnitBasis) || 0) === unitBasis) return;
    items[existingIdx] = { ...cur, Qty: qty, UnitBasis: unitBasis };
  } else {
    let item: Record<string, any>;
    if (kind === "Sealed") {
      item = {
        "AsType":      "Sealed",
        "Game":        row["Game"] || "",
        "Item":        row["Product"] || "",
        "Set":         row["Set"] || "",
        "Number":      "",
        "Condition":   "",
        "ProductType": row["Product Type"] || "",
        "Qty":         qty,
        "UnitBasis":   unitBasis,
        "Lot ID":      lotId,
      };
    } else {
      item = {
        "AsType":      "Card",
        "Game":        row["Game"] || "",
        "Item":        row["Card Name"] || "",
        "Set":         row["Set"] || "",
        "Number":      row["Card #"] || "",
        "Condition":   row["Condition/Grade"] || "",
        "ProductType": "",
        "Qty":         qty,
        "UnitBasis":   unitBasis,
        "Lot ID":      lotId,
      };
    }
    items.push(item);
  }

  DB.update("transactions", "Txn ID", sourceId, { "Items": items });
}
